import { useState, memo } from "react";
import {
  Eye, EyeSlash, LockSimple, LockSimpleOpen, DotsSixVertical, Stack,
  CaretRight, CaretDown, TextT, Image as ImageIcon, Square, Circle, Minus, FilmSlate,
  FolderSimple, FolderSimplePlus, Trash, PencilSimple,
} from "@phosphor-icons/react";

function layerLabel(el) {
  if (el.name) return el.name;
  if (el.type === "text") return (el.text || "Text").split("\n")[0].slice(0, 22) || "Text";
  if (el.type === "image") return "Image";
  if (el.type === "video") return "Video overlay";
  if (el.type === "rect") return "Rectangle";
  if (el.type === "ellipse") return "Ellipse";
  if (el.type === "line") return "Line";
  return "Layer";
}

const Thumb = memo(function Thumb({ el }) {
  const base = "w-9 h-9 rounded-md overflow-hidden shrink-0 border border-white/10 flex items-center justify-center bg-[#0E0E0E]";
  if (el.type === "image") return <div className={base}><img src={el.src} alt="" crossOrigin="anonymous" loading="lazy" className="w-full h-full object-cover" /></div>;
  if (el.type === "video") return <div className={base + " relative"}><video src={el.src} muted playsInline preload="metadata" className="w-full h-full object-cover" /><FilmSlate size={11} className="absolute bottom-0.5 right-0.5 text-white/80" /></div>;
  if (el.type === "text") return <div className={base} style={{ color: el.color }}><TextT weight="bold" size={18} /></div>;
  if (el.type === "rect") return <div className={base}><div style={{ width: 18, height: 18, background: el.fill || "#666", borderRadius: 3 }} /></div>;
  if (el.type === "ellipse") return <div className={base}><div style={{ width: 18, height: 18, background: el.fill || "#666", borderRadius: "50%" }} /></div>;
  if (el.type === "line") return <div className={base}><div style={{ width: 20, height: 3, background: el.fill || "#666", borderRadius: 2 }} /></div>;
  return <div className={base}><Square size={16} /></div>;
}, (a, b) => a.el.type === b.el.type && a.el.src === b.el.src && a.el.fill === b.el.fill && a.el.color === b.el.color);

function LayersPanel({
  layers, folders = [], selectedIds = [], open, onToggleOpen,
  onSelectMany, onToggleVisibility, onToggleLock, onReorder, onRename, onUpdateText,
  onCreateFolder, onRenameFolder, onDeleteFolder, onToggleFolderCollapse,
  onToggleFolderVisibility, onAssignFolder,
}) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overFolder, setOverFolder] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [textEditId, setTextEditId] = useState(null);
  const [textEditVal, setTextEditVal] = useState("");
  const [renameFolderId, setRenameFolderId] = useState(null);
  const [folderRenameVal, setFolderRenameVal] = useState("");
  const [anchor, setAnchor] = useState(null);

  const commitTextEdit = () => {
    if (textEditId != null && onUpdateText) onUpdateText(textEditId, textEditVal);
    setTextEditId(null);
  };

  const handleRowClick = (e, id) => {
    const idx = layers.findIndex((l) => l.id === id);
    if (e.shiftKey && anchor != null) {
      const a = Math.min(anchor, idx), b = Math.max(anchor, idx);
      onSelectMany(layers.slice(a, b + 1).map((l) => l.id));
    } else if (e.ctrlKey || e.metaKey) {
      const set = new Set(selectedIds);
      set.has(id) ? set.delete(id) : set.add(id);
      onSelectMany([...set]);
      setAnchor(idx);
    } else {
      onSelectMany([id]);
      setAnchor(idx);
    }
  };

  if (!open) {
    return (
      <div className="w-9 border-l border-white/10 bg-[#121212] flex flex-col items-center pt-3 shrink-0 z-30">
        <button onClick={onToggleOpen} data-testid="layers-expand-btn" title="Show layers" className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-zinc-300">
          <Stack size={18} weight="fill" />
        </button>
        <span className="mt-3 text-[10px] tracking-[0.2em] text-zinc-500 font-semibold" style={{ writingMode: "vertical-rl" }}>LAYERS</span>
      </div>
    );
  }

  const idsToMove = () => (dragId && selectedIds.includes(dragId) ? selectedIds : dragId ? [dragId] : []);

  const handleDrop = (targetId) => {
    if (dragId && dragId !== targetId) {
      const arr = [...layers];
      const from = arr.findIndex((l) => l.id === dragId);
      const to = arr.findIndex((l) => l.id === targetId);
      if (from !== -1 && to !== -1) {
        const [moved] = arr.splice(from, 1);
        arr.splice(to, 0, moved);
        onReorder(arr);
      }
    }
    setDragId(null); setOverId(null);
  };

  const dropToFolder = (fid) => {
    const ids = idsToMove();
    if (ids.length) onAssignFolder(ids, fid);
    setDragId(null); setOverId(null); setOverFolder(null);
  };

  const commitRename = () => {
    if (renameId) onRename(renameId, renameVal.trim());
    setRenameId(null);
  };
  const commitFolderRename = () => {
    if (renameFolderId) onRenameFolder(renameFolderId, folderRenameVal.trim());
    setRenameFolderId(null);
  };

  const LayerRow = ({ el, nested }) => {
    const isSel = selectedIds.includes(el.id);
    return (
      <div
        draggable={renameId !== el.id && textEditId !== el.id}
        onDragStart={() => setDragId(el.id)}
        onDragOver={(e) => { e.preventDefault(); setOverId(el.id); }}
        onDrop={() => handleDrop(el.id)}
        onDragEnd={() => { setDragId(null); setOverId(null); setOverFolder(null); }}
        onClick={(e) => handleRowClick(e, el.id)}
        data-testid={`layer-row-${el.id}`}
        className={`group flex items-center gap-1.5 px-1.5 py-1.5 rounded-lg mb-1 cursor-pointer border transition-colors ${nested ? "ml-3" : ""} ${
          isSel ? "bg-blue-600/20 border-blue-500/50" : "border-transparent hover:bg-white/5"
        } ${overId === el.id && dragId !== el.id ? "border-t-2 border-t-fuchsia-500" : ""} ${el.hidden ? "opacity-50" : ""}`}
      >
        <DotsSixVertical size={15} className="text-zinc-600 group-hover:text-zinc-400 shrink-0 cursor-grab" />
        <Thumb el={el} />
        {textEditId === el.id ? (
          <textarea
            autoFocus
            value={textEditVal}
            onChange={(e) => setTextEditVal(e.target.value)}
            onBlur={commitTextEdit}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) commitTextEdit(); if (e.key === "Escape") setTextEditId(null); }}
            onClick={(e) => e.stopPropagation()}
            rows={2}
            placeholder="Edit text…"
            data-testid={`layer-text-input-${el.id}`}
            className="flex-1 min-w-0 rounded bg-black/50 border border-blue-500/60 px-1.5 py-1 text-xs text-white outline-none resize-none leading-snug"
          />
        ) : renameId === el.id ? (
          <input
            autoFocus
            value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenameId(null); }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`layer-rename-input-${el.id}`}
            className="flex-1 min-w-0 h-7 rounded bg-black/50 border border-white/15 px-1.5 text-xs text-white outline-none"
          />
        ) : (
          <span
            className="flex-1 min-w-0 text-xs truncate text-zinc-200"
            onDoubleClick={(e) => {
              e.stopPropagation();
              if (el.type === "text") { setTextEditId(el.id); setTextEditVal(el.text || ""); }
              else { setRenameId(el.id); setRenameVal(layerLabel(el)); }
            }}
            title={el.type === "text" ? "Double-click to edit text" : layerLabel(el)}
          >
            {layerLabel(el)}
          </span>
        )}
        {el.type === "text" && (
          <button
            onClick={(e) => { e.stopPropagation(); setTextEditId(el.id); setTextEditVal(el.text || ""); }}
            data-testid={`layer-edit-text-${el.id}`}
            title="Edit text content"
            className="h-6 w-6 rounded flex items-center justify-center shrink-0 text-zinc-400 hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-all"
          >
            <TextT size={14} weight="bold" />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setRenameId(el.id); setRenameVal(el.name || layerLabel(el)); }}
          data-testid={`layer-rename-${el.id}`}
          title="Rename layer"
          className="h-6 w-6 rounded flex items-center justify-center shrink-0 text-zinc-500 hover:text-white opacity-0 group-hover:opacity-100 transition-all"
        >
          <PencilSimple size={14} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleLock(el.id); }}
          data-testid={`layer-lock-${el.id}`}
          title={el.locked ? "Unlock layer" : "Lock layer (click-through)"}
          className={`h-6 w-6 rounded flex items-center justify-center shrink-0 transition-colors ${el.locked ? "text-amber-400" : "text-zinc-500 hover:text-white opacity-0 group-hover:opacity-100"}`}
        >
          {el.locked ? <LockSimple size={14} weight="fill" /> : <LockSimpleOpen size={14} />}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleVisibility(el.id); }}
          data-testid={`layer-vis-${el.id}`}
          title={el.hidden ? "Show layer" : "Hide layer"}
          className={`h-6 w-6 rounded flex items-center justify-center shrink-0 transition-colors ${el.hidden ? "text-zinc-500" : "text-zinc-300 hover:text-white"}`}
        >
          {el.hidden ? <EyeSlash size={15} /> : <Eye size={15} />}
        </button>
      </div>
    );
  };

  const folderIds = new Set(folders.map((f) => f.id));
  const ungrouped = layers.filter((el) => !el.folderId || !folderIds.has(el.folderId));

  return (
    <div className="w-60 border-l border-white/10 bg-[#121212] flex flex-col shrink-0 z-30" data-testid="layers-panel">
      <div className="h-11 flex items-center justify-between px-3 border-b border-white/10">
        <span className="text-sm font-semibold flex items-center gap-1.5"><Stack size={16} weight="fill" /> Layers</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onCreateFolder(selectedIds.length ? selectedIds : [])}
            data-testid="layers-new-folder-btn"
            title={selectedIds.length ? "Group selected layers into a new folder" : "Create a new folder"}
            className="h-7 w-7 rounded-lg hover:bg-white/10 flex items-center justify-center text-zinc-300"
          >
            <FolderSimplePlus size={16} weight="bold" />
          </button>
          <button onClick={onToggleOpen} data-testid="layers-collapse-btn" title="Hide layers panel" className="h-7 w-7 rounded-lg hover:bg-white/10 flex items-center justify-center">
            <CaretRight size={15} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-1.5">
        {layers.length === 0 && (
          <p className="text-xs text-zinc-500 text-center py-8 px-3">No layers yet. Add text, shapes, images or overlays to see them here.</p>
        )}

        {/* Folders */}
        {folders.map((f) => {
          const members = layers.filter((el) => el.folderId === f.id);
          const anyVisible = members.some((el) => !el.hidden);
          return (
            <div key={f.id} className="mb-1.5" data-testid={`folder-${f.id}`}>
              <div
                onDragOver={(e) => { e.preventDefault(); setOverFolder(f.id); }}
                onDragLeave={() => setOverFolder((cur) => (cur === f.id ? null : cur))}
                onDrop={() => dropToFolder(f.id)}
                className={`group flex items-center gap-1 px-1.5 py-1.5 rounded-lg border transition-colors ${
                  overFolder === f.id ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                }`}
              >
                <button
                  onClick={() => onToggleFolderCollapse(f.id)}
                  data-testid={`folder-toggle-${f.id}`}
                  className="h-5 w-5 rounded flex items-center justify-center text-zinc-400 hover:text-white shrink-0"
                >
                  {f.collapsed ? <CaretRight size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
                </button>
                <FolderSimple size={16} weight="fill" className="text-amber-300 shrink-0" />
                {renameFolderId === f.id ? (
                  <input
                    autoFocus
                    value={folderRenameVal}
                    onChange={(e) => setFolderRenameVal(e.target.value)}
                    onBlur={commitFolderRename}
                    onKeyDown={(e) => { if (e.key === "Enter") commitFolderRename(); if (e.key === "Escape") setRenameFolderId(null); }}
                    data-testid={`folder-rename-input-${f.id}`}
                    className="flex-1 min-w-0 h-6 rounded bg-black/50 border border-white/15 px-1.5 text-xs text-white outline-none"
                  />
                ) : (
                  <span
                    className="flex-1 min-w-0 text-xs font-semibold truncate text-zinc-100"
                    onDoubleClick={() => { setRenameFolderId(f.id); setFolderRenameVal(f.name); }}
                    title={`${f.name} — double-click to rename`}
                  >
                    {f.name}
                  </span>
                )}
                <span className="text-[10px] text-zinc-500 shrink-0">{members.length}</span>
                <button
                  onClick={() => onToggleFolderVisibility(f.id)}
                  data-testid={`folder-vis-${f.id}`}
                  title={anyVisible ? "Hide all in folder" : "Show all in folder"}
                  className={`h-6 w-6 rounded flex items-center justify-center shrink-0 transition-colors ${anyVisible ? "text-zinc-300 hover:text-white" : "text-zinc-500"}`}
                >
                  {anyVisible ? <Eye size={14} /> : <EyeSlash size={14} />}
                </button>
                <button
                  onClick={() => onDeleteFolder(f.id)}
                  data-testid={`folder-delete-${f.id}`}
                  title="Delete folder (keeps layers)"
                  className="h-6 w-6 rounded flex items-center justify-center shrink-0 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Trash size={14} />
                </button>
              </div>
              {!f.collapsed && (
                <div className="mt-1">
                  {members.length === 0 ? (
                    <p className="text-[10px] text-zinc-600 italic px-3 py-2 ml-3">Drag layers here</p>
                  ) : (
                    members.map((el) => <LayerRow key={el.id} el={el} nested />)
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Ungrouped layers */}
        {folders.length > 0 && ungrouped.length > 0 && (
          <div
            onDragOver={(e) => { e.preventDefault(); setOverFolder("__root__"); }}
            onDragLeave={() => setOverFolder((cur) => (cur === "__root__" ? null : cur))}
            onDrop={() => dropToFolder(null)}
            data-testid="folder-ungrouped-drop"
            className={`text-[10px] uppercase tracking-wider px-2 py-1.5 mb-1 rounded ${overFolder === "__root__" ? "bg-fuchsia-500/10 text-fuchsia-300" : "text-zinc-500"}`}
          >
            Ungrouped
          </div>
        )}
        {ungrouped.map((el) => <LayerRow key={el.id} el={el} nested={false} />)}
      </div>
      <p className="text-[10px] text-zinc-500 px-3 py-2 border-t border-white/10 leading-relaxed">
        Drag layers onto a folder to organize · double-click to rename · new-folder icon groups your selection.
      </p>
    </div>
  );
}

export default memo(LayersPanel);
